import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import axios from "axios";
import { Textarea } from "./ui/textarea";
import { Send, Sparkles, Zap, Hand } from "lucide-react";
import { ShareMood } from "./ShareMood";
import { useWeather } from "./WeatherWidget";
import { useMoodTheme } from "../contexts/MoodThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Full mood palette for the wheel (26 emojis)
const MOODS = [
  // Positive moods
  { id: "very_happy", emoji: "😄", label: "Muy Feliz", color: "#10B981" },
  { id: "happy", emoji: "🙂", label: "Bien", color: "#84CC16" },
  { id: "excited", emoji: "🤩", label: "Emocionado", color: "#F59E0B" },
  { id: "celebrating", emoji: "🥳", label: "Celebrando", color: "#EC4899" },
  { id: "confident", emoji: "😎", label: "Confiado", color: "#8B5CF6" },
  { id: "in_love", emoji: "😍", label: "Enamorado", color: "#F472B6" },
  { id: "relaxed", emoji: "😌", label: "Relajado", color: "#22D3EE" },
  { id: "affectionate", emoji: "🤗", label: "Cariñoso", color: "#FB923C" },
  { id: "grateful", emoji: "🙏", label: "Agradecido", color: "#A78BFA" },
  // Neutral moods
  { id: "neutral", emoji: "😐", label: "Neutral", color: "#FBBF24" },
  { id: "thoughtful", emoji: "🤔", label: "Pensativo", color: "#C084FC" },
  { id: "surprised", emoji: "😲", label: "Sorprendido", color: "#FACC15" },
  { id: "butterflies", emoji: "🦋", label: "Mariposas", color: "#38BDF8" },
  { id: "mischievous", emoji: "😏", label: "Pícaro", color: "#F97316" },
  { id: "ironic", emoji: "🙃", label: "Irónico", color: "#E879F9" },
  // Negative moods
  { id: "sad", emoji: "😔", label: "Triste", color: "#60A5FA" },
  { id: "very_sad", emoji: "😢", label: "Muy Triste", color: "#818CF8" },
  { id: "angry", emoji: "😡", label: "Enojado", color: "#F87171" },
  { id: "anxious", emoji: "😰", label: "Ansioso", color: "#FDBA74" },
  { id: "frustrated", emoji: "😤", label: "Frustrado", color: "#EF4444" },
  { id: "vulnerable", emoji: "🥺", label: "Vulnerable", color: "#C4B5FD" },
  { id: "overwhelmed", emoji: "🤯", label: "Abrumado", color: "#F43F5E" },
  // Physical states
  { id: "tired", emoji: "😴", label: "Cansado", color: "#94A3B8" },
  { id: "sick", emoji: "🤒", label: "Enfermo", color: "#2DD4BF" },
  { id: "sporty", emoji: "💪", label: "Deportista", color: "#FB7185" },
  { id: "melted", emoji: "🫠", label: "Derretido", color: "#FCD34D" },
];

// Neon light bulb component
const NeonBulb = ({ index, total, isActive, color, isSpinning, radius }) => {
  const angle = (index / total) * 360 - 90;
  const x = Math.cos((angle * Math.PI) / 180) * radius;
  const y = Math.sin((angle * Math.PI) / 180) * radius;

  return (
    <motion.div
      className="absolute w-3 h-3 sm:w-4 sm:h-4 rounded-full"
      style={{
        left: `calc(50% + ${x}px - 6px)`,
        top: `calc(50% + ${y}px - 6px)`,
        backgroundColor: isActive ? color : "#1a1a2e",
        boxShadow: isActive 
          ? `0 0 10px ${color}, 0 0 20px ${color}, 0 0 30px ${color}80`
          : "0 0 2px rgba(255,255,255,0.2)",
        border: `1px solid ${isActive ? color : 'rgba(255,255,255,0.1)'}`,
      }}
      animate={isSpinning ? {
        backgroundColor: [color, "#1a1a2e", color],
        boxShadow: [
          `0 0 10px ${color}, 0 0 20px ${color}`,
          "0 0 2px rgba(255,255,255,0.2)",
          `0 0 10px ${color}, 0 0 20px ${color}`,
        ],
      } : {}}
      transition={isSpinning ? {
        duration: 0.1,
        repeat: Infinity,
        delay: index * 0.015,
      } : { duration: 0.3 }}
    />
  );
};

// Wheel segment component
const WheelSegment = ({ mood, index, total, isSelected }) => {
  const segmentAngle = 360 / total;
  const startAngle = index * segmentAngle - 90;
  const endAngle = startAngle + segmentAngle;
  
  const radius = 180;
  const innerRadius = 60;
  
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;
  
  const x1 = 200 + radius * Math.cos(startRad);
  const y1 = 200 + radius * Math.sin(startRad);
  const x2 = 200 + radius * Math.cos(endRad);
  const y2 = 200 + radius * Math.sin(endRad);
  const x3 = 200 + innerRadius * Math.cos(endRad);
  const y3 = 200 + innerRadius * Math.sin(endRad);
  const x4 = 200 + innerRadius * Math.cos(startRad);
  const y4 = 200 + innerRadius * Math.sin(startRad);
  
  const largeArc = segmentAngle > 180 ? 1 : 0;
  
  const path = `
    M ${x1} ${y1}
    A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
    L ${x3} ${y3}
    A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
    Z
  `;
  
  const midAngle = startAngle + segmentAngle / 2;
  const midRad = (midAngle * Math.PI) / 180;
  const emojiRadius = (radius + innerRadius) / 2 + 8;
  const emojiX = 200 + emojiRadius * Math.cos(midRad);
  const emojiY = 200 + emojiRadius * Math.sin(midRad);

  return (
    <g>
      <motion.path
        d={path}
        fill={isSelected ? `${mood.color}50` : `${mood.color}25`}
        stroke={mood.color}
        strokeWidth={isSelected ? 4 : 1.5}
        animate={{
          fill: isSelected ? `${mood.color}70` : `${mood.color}25`,
          filter: isSelected ? `drop-shadow(0 0 15px ${mood.color})` : "none",
        }}
        transition={{ duration: 0.3 }}
      />
      
      <text
        x={emojiX}
        y={emojiY}
        textAnchor="middle"
        dominantBaseline="middle"
        className="select-none pointer-events-none"
        style={{
          fontSize: isSelected ? "22px" : "18px",
          filter: isSelected ? `drop-shadow(0 0 10px ${mood.color})` : "none",
          transform: `rotate(${midAngle + 90}deg)`,
          transformOrigin: `${emojiX}px ${emojiY}px`,
        }}
      >
        {mood.emoji}
      </text>
    </g>
  );
};

// Main Draggable Roulette Wheel Component
const DraggableRouletteWheel = ({ onMoodSelect, selectedMood, rotation, setRotation, isSpinning, setIsSpinning }) => {
  const wheelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const lastAngleRef = useRef(0);
  const lastTimeRef = useRef(0);
  const animationRef = useRef(null);
  
  const totalBulbs = 40;
  const bulbColors = MOODS.map(m => m.color);
  const [activeBulbIndex, setActiveBulbIndex] = useState(0);

  // Calculate angle from center of wheel
  const getAngleFromCenter = useCallback((clientX, clientY) => {
    if (!wheelRef.current) return 0;
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }, []);

  // Get selected mood based on rotation
  const getSelectedMoodFromRotation = useCallback((rot) => {
    const segmentAngle = 360 / MOODS.length;
    const normalizedRotation = ((rot % 360) + 360) % 360;
    const selectedIndex = Math.floor(((360 - normalizedRotation + segmentAngle / 2) % 360) / segmentAngle) % MOODS.length;
    return MOODS[selectedIndex];
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((clientX, clientY) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setIsDragging(true);
    setIsSpinning(true);
    const angle = getAngleFromCenter(clientX, clientY);
    setStartAngle(angle - rotation);
    lastAngleRef.current = rotation;
    lastTimeRef.current = Date.now();
  }, [getAngleFromCenter, rotation, setIsSpinning]);

  // Handle drag move
  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    
    const currentAngle = getAngleFromCenter(clientX, clientY);
    const newRotation = currentAngle - startAngle;
    
    // Calculate velocity
    const now = Date.now();
    const dt = now - lastTimeRef.current;
    if (dt > 0) {
      const dAngle = newRotation - lastAngleRef.current;
      setVelocity(dAngle / dt * 16); // Normalize to ~60fps
    }
    
    lastAngleRef.current = newRotation;
    lastTimeRef.current = now;
    setRotation(newRotation);
  }, [isDragging, getAngleFromCenter, startAngle, setRotation]);

  // Handle drag end with momentum
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Apply momentum
    let currentVelocity = velocity;
    let currentRotation = rotation;
    const friction = 0.94; // Tighter friction for faster stop
    const minVelocity = 0.1;
    
    const animate = () => {
      currentVelocity *= friction;
      currentRotation += currentVelocity;
      setRotation(currentRotation);
      
      if (Math.abs(currentVelocity) > minVelocity) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Stopped - select the mood
        setIsSpinning(false);
        const selectedMoodData = getSelectedMoodFromRotation(currentRotation);
        onMoodSelect(selectedMoodData.id);
      }
    };
    
    if (Math.abs(currentVelocity) > minVelocity) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      setIsSpinning(false);
      const selectedMoodData = getSelectedMoodFromRotation(currentRotation);
      onMoodSelect(selectedMoodData.id);
    }
  }, [isDragging, velocity, rotation, setRotation, setIsSpinning, getSelectedMoodFromRotation, onMoodSelect]);

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };
  
  const handleMouseMove = useCallback((e) => {
    handleDragMove(e.clientX, e.clientY);
  }, [handleDragMove]);
  
  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch events
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };
  
  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  }, [handleDragMove]);
  
  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Add/remove global event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Animate bulbs during spin
  useEffect(() => {
    if (isSpinning) {
      const interval = setInterval(() => {
        setActiveBulbIndex(prev => (prev + 1) % totalBulbs);
      }, 40);
      return () => clearInterval(interval);
    }
  }, [isSpinning]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const currentMoodAtPointer = getSelectedMoodFromRotation(rotation);

  return (
    <div className="relative w-[340px] h-[340px] sm:w-[420px] sm:h-[420px] mx-auto">
      {/* Outer glow ring */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          boxShadow: isSpinning
            ? [
                "0 0 40px rgba(0,243,255,0.6), 0 0 80px rgba(168,85,247,0.4)",
                "0 0 60px rgba(168,85,247,0.6), 0 0 100px rgba(0,243,255,0.4)",
                "0 0 40px rgba(0,243,255,0.6), 0 0 80px rgba(168,85,247,0.4)",
              ]
            : "0 0 30px rgba(0,243,255,0.4), 0 0 60px rgba(168,85,247,0.3)",
        }}
        transition={{ duration: 0.4, repeat: isSpinning ? Infinity : 0 }}
      />

      {/* Neon bulbs around the wheel */}
      {Array.from({ length: totalBulbs }).map((_, i) => (
        <NeonBulb
          key={i}
          index={i}
          total={totalBulbs}
          isActive={isSpinning ? i === activeBulbIndex || i === (activeBulbIndex + totalBulbs/2) % totalBulbs : true}
          color={bulbColors[i % bulbColors.length]}
          isSpinning={isSpinning}
          radius={165}
        />
      ))}

      {/* Main wheel container */}
      <div 
        ref={wheelRef}
        className="absolute inset-4 sm:inset-5 rounded-full bg-gradient-to-br from-slate-900 to-slate-800 border-4 border-neon-cyan/40 overflow-hidden cursor-grab active:cursor-grabbing touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* SVG Wheel */}
        <svg
          viewBox="0 0 400 400"
          className="w-full h-full"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {MOODS.map((mood, index) => (
            <WheelSegment
              key={mood.id}
              mood={mood}
              index={index}
              total={MOODS.length}
              isSelected={selectedMood === mood.id}
            />
          ))}
        </svg>

        {/* Drag hint overlay - only show when not spinning and no selection */}
        {!isSpinning && !selectedMood && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="bg-black/60 rounded-2xl px-4 py-2 flex items-center gap-2">
              <Hand className="w-5 h-5 text-neon-cyan animate-pulse" />
              <span className="text-sm text-white font-rajdhani">Arrastra para girar</span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Center hub */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border-4 border-neon-purple/60 flex items-center justify-center"
          animate={{
            boxShadow: isSpinning
              ? [
                  "0 0 25px rgba(168,85,247,0.7), inset 0 0 25px rgba(0,243,255,0.4)",
                  "0 0 50px rgba(0,243,255,0.7), inset 0 0 35px rgba(168,85,247,0.4)",
                  "0 0 25px rgba(168,85,247,0.7), inset 0 0 25px rgba(0,243,255,0.4)",
                ]
              : "0 0 20px rgba(168,85,247,0.5), inset 0 0 20px rgba(0,243,255,0.3)",
          }}
          transition={{ duration: 0.3, repeat: isSpinning ? Infinity : 0 }}
        >
          <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-neon-cyan" />
        </motion.div>
      </div>

      {/* Pointer/indicator at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1">
        <motion.div
          className="relative"
          animate={{
            filter: isSpinning
              ? ["drop-shadow(0 0 15px #00f3ff)", "drop-shadow(0 0 25px #00f3ff)", "drop-shadow(0 0 15px #00f3ff)"]
              : "drop-shadow(0 0 10px #00f3ff)",
          }}
          transition={{ duration: 0.3, repeat: isSpinning ? Infinity : 0 }}
        >
          <div className="w-0 h-0 border-l-[18px] border-r-[18px] border-t-[30px] border-l-transparent border-r-transparent border-t-neon-cyan" />
        </motion.div>
      </div>

      {/* Current mood preview at pointer */}
      <AnimatePresence>
        {(isDragging || isSpinning) && currentMoodAtPointer && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute -top-16 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-black/80 border"
            style={{ borderColor: currentMoodAtPointer.color }}
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl">{currentMoodAtPointer.emoji}</span>
              <span className="text-sm font-rajdhani" style={{ color: currentMoodAtPointer.color }}>
                {currentMoodAtPointer.label}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const MoodSelector = ({ onMoodSaved, initialMood = null, initialNote = "", onCancel = null }) => {
  const [selectedMood, setSelectedMood] = useState(initialMood);
  const [note, setNote] = useState(initialNote);
  const [saving, setSaving] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [lastSavedMood, setLastSavedMood] = useState(null);
  const [lastSavedNote, setLastSavedNote] = useState("");
  
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [showWinAnimation, setShowWinAnimation] = useState(false);
  
  const currentWeather = useWeather();
  const { setMoodTheme } = useMoodTheme();

  const selectedMoodData = MOODS.find(m => m.id === selectedMood);

  const handleMoodSelect = (moodId) => {
    setSelectedMood(moodId);
    setShowWinAnimation(true);
    setTimeout(() => setShowWinAnimation(false), 2000);
  };

  const handleSave = async () => {
    if (!selectedMood) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/moods`, {
        mood: selectedMood,
        note: note.trim() || null,
        weather: currentWeather || null,
      });
      
      setMoodTheme(selectedMood);
      setLastSavedMood(selectedMood);
      setLastSavedNote(note.trim());
      setShowShare(true);
      setSelectedMood(null);
      setNote("");
      onMoodSaved?.();
    } catch (error) {
      console.error("Error saving mood:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Roulette Section */}
      <div className="relative">
        {/* Background ambient glow */}
        <motion.div
          className="absolute inset-0 -m-8 rounded-3xl pointer-events-none"
          animate={{
            background: selectedMoodData
              ? `radial-gradient(ellipse at center, ${selectedMoodData.color}30 0%, transparent 60%)`
              : "radial-gradient(ellipse at center, rgba(0,243,255,0.1) 0%, transparent 60%)",
            opacity: isSpinning ? [0.5, 1, 0.5] : 0.8,
          }}
          transition={{ duration: isSpinning ? 0.5 : 0.3, repeat: isSpinning ? Infinity : 0 }}
          style={{ filter: "blur(40px)" }}
        />

        {/* Draggable Roulette Wheel */}
        <DraggableRouletteWheel
          selectedMood={selectedMood}
          onMoodSelect={handleMoodSelect}
          rotation={rotation}
          setRotation={setRotation}
          isSpinning={isSpinning}
          setIsSpinning={setIsSpinning}
        />

        {/* Win Animation */}
        <AnimatePresence>
          {showWinAnimation && selectedMoodData && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
              <motion.div
                className="px-8 py-5 rounded-2xl bg-black/90 border-2"
                style={{
                  borderColor: selectedMoodData.color,
                  boxShadow: `0 0 50px ${selectedMoodData.color}, 0 0 100px ${selectedMoodData.color}50`,
                }}
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                <div className="flex items-center gap-4">
                  <motion.span
                    className="text-6xl"
                    animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: 2 }}
                  >
                    {selectedMoodData.emoji}
                  </motion.span>
                  <div>
                    <p className="text-sm text-slate-400 font-rajdhani">¡Seleccionaste!</p>
                    <p
                      className="text-2xl font-orbitron font-bold"
                      style={{ color: selectedMoodData.color }}
                    >
                      {selectedMoodData.label}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Selected mood indicator */}
      <AnimatePresence>
        {selectedMood && selectedMoodData && !showWinAnimation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center justify-center gap-3 py-4 px-6 rounded-2xl border-2"
            style={{
              backgroundColor: `${selectedMoodData.color}15`,
              borderColor: selectedMoodData.color,
              boxShadow: `0 0 30px ${selectedMoodData.color}40`,
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: selectedMoodData.color }} />
            <span
              className="font-rajdhani font-bold text-lg"
              style={{ color: selectedMoodData.color }}
            >
              Te sientes: {selectedMoodData.label}
            </span>
            <span className="text-3xl">{selectedMoodData.emoji}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Note Input */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="space-y-4"
      >
        <Textarea
          placeholder="Añade una nota sobre cómo te sientes... (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="mood-note-input"
          className="bg-black/50 border-white/10 focus:border-neon-cyan/50 focus:ring-1 focus:ring-neon-cyan/50 text-white placeholder:text-slate-600 font-inter min-h-[100px] resize-none rounded-xl"
        />

        <div className="flex gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              data-testid="mood-cancel-btn"
              className="flex-1 py-3 rounded-xl border border-neon-purple/50 text-neon-purple font-rajdhani font-bold uppercase tracking-wider hover:bg-neon-purple/10 transition-all"
            >
              Cancelar
            </button>
          )}
          <motion.button
            onClick={handleSave}
            disabled={!selectedMood || saving || isSpinning}
            data-testid="mood-save-btn"
            whileHover={selectedMood ? { scale: 1.02 } : {}}
            whileTap={selectedMood ? { scale: 0.98 } : {}}
            className={`flex-1 flex items-center justify-center gap-2 font-rajdhani font-bold uppercase tracking-widest px-6 py-4 rounded-xl transition-all duration-300 ${
              selectedMood && !isSpinning
                ? "text-white border-2"
                : "bg-slate-800/50 text-slate-500 border border-slate-700 cursor-not-allowed"
            }`}
            style={selectedMood && selectedMoodData && !isSpinning ? {
              backgroundColor: `${selectedMoodData.color}25`,
              borderColor: selectedMoodData.color,
              boxShadow: `0 0 30px ${selectedMoodData.color}50`,
              color: selectedMoodData.color,
            } : {}}
          >
            <Send className="w-5 h-5" />
            {saving ? "Guardando..." : "Registrar Estado"}
          </motion.button>
        </div>
      </motion.div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShare && lastSavedMood && (
          <ShareMood
            mood={lastSavedMood}
            note={lastSavedNote}
            date={new Date().toISOString()}
            onClose={() => setShowShare(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
